import { useEffect, useRef, useState } from "react";
import {
  Box,
  Checkbox,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Typography,
  TextField,
  IconButton,
  InputAdornment,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Tooltip,
} from "@mui/material";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import ForwardToInboxIcon from "@mui/icons-material/ForwardToInbox";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import { useSelector } from "react-redux";
import Popup from "../../../../Base/views/Popup";
import { formatPayDatePeriod } from "../../../../Base/helper/baseFunctions";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import ReportEmailPopup from "../../Reports/ReportEmailPopup";
import SelectableMultiSelect from "../../../../Base/views/SelectableMultiSelect";

const PayrunApprovedRegular = ({
  selectedYear ,
  selectedMonth,
  paydate,
  wageReportData,
  isLoading,
  filters,
  handleFilterChange,
  handleWorkLoacation,
  worklocationData
}) => {
  const { open, fixedMenu, isMobile } = useSelector(
    (state) => state?.MenuReducer
  );
  const date = new Date();
  const [openPopup, setOpenPopup] = useState(false);
  const [name, setName] = useState(
    date.toLocaleString("default", { month: "short", year: "numeric" })
  );
  const [anchorEl, setAnchorEl] = useState(null);
  const [columns, setColumns] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const anchorRef = useRef(null);

  const flattenData = (data) => {
    const flatObject = {};
    const excludedKeys = [
      "employees_state_insurance_corporation",
      "employer_contribution_to_pf",
    ];

    Object.keys(data).forEach((key) => {
      if (typeof data[key] === "object" && data[key] !== null) {
        if (key === "earned_salary_json" || key === "actual_earnings_json") {
          const prefix = key === "earned_salary_json" ? "earned_" : "actual_";
          Object.keys(data[key])?.forEach((nestedKey) => {
            if (!excludedKeys.includes(nestedKey)) {
              // remove ESIC and EPf from earned and actual
              flatObject[`${prefix}${nestedKey}`] = data[key][nestedKey];
            }
          });
        } else if (
          key === "employee_post_adjustment_component_earning_json" ||
          key === "employee_post_adjustment_component_deduction_json"
        ) {
          const components = data[key] || [];
          const suffix =
            key === "employee_post_adjustment_component_earning_json"
              ? "_(Adjusted_earnings)"
              : "_(Adjusted_deductions)";
          components?.forEach((component) => {
            flatObject[`${component?.label?.replace(/\s+/g, "_")}${suffix}`] =
              component?.amount;
          });
        } else if (
          key === "employee_ecms_adjustment_component_earning_json" ||
          key === "employee_ecms_adjustment_component_deduction_json"
        ) {
          const components = data[key] || [];
          const suffix =
            key === "employee_ecms_adjustment_component_earning_json"
              ? "_(Ecms_earnings)"
              : "_(Ecms_deductions)";
          components?.forEach((component) => {
            flatObject[`${component?.label?.replace(/\s+/g, "_")}${suffix}`] =
              component?.approver_amount;
          });
        } else {
          Object.keys(data[key]).forEach((nestedKey) => {
            flatObject[nestedKey] = data[key][nestedKey];
          });
        }
      } else {
        flatObject[key] = data[key];
      }
    });
    return flatObject;
  };

  const getRelevantFields = (data) => {
    if (!data || data.length === 0) return [];
    const defaultFields = {
      employee_no: "Employee No",
      employee_name: "Employee Name",
      days_to_be_paid: "Paid Days",
      gross_earned_salary_month: "Total Earned Salary",
      total_deductions: "Total Deductions",
      net_salary: "Net Salary",
    };
    const flattenedData = data.map((item) => flattenData(item));
    const allFields = new Set();
    flattenedData.forEach((record) => {
      Object.keys(record).forEach((key) => {
        if (
          ![
            "pay_summary_pk",
            "employee_fk",
            "createdby",
            "createdon",
            "modifiedby",
            "modifiedon",
          ].includes(key)
        ) {
          allFields.add(key);
        }
      });
    });
    const hasValidValues = (field) => {
      return flattenedData.some((record) => {
        const value = record[field];
        return (
          value !== null &&
          value !== undefined &&
          !(typeof value === "string" && value.trim() === "") &&
          Number(value) !== 0
        );
      });
    };
    const formatFieldName = (field) => {
      const prefixMatch = field.match(/^(actual_|earned_)/);
      const prefix = prefixMatch
        ? prefixMatch[0].replace("_", "").charAt(0).toUpperCase() +
          prefixMatch[0].slice(1).toLowerCase()
        : "";
      const formattedName = field
        .replace(/^(actual_|earned_)/, "")
        .replace(/(json_)/gi, "");
      return `${prefix} ${formattedName}`
        .split(/[_\s]+/)
        .filter((word) => word.trim() !== "")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
        .trim();
    };
    return Array.from(allFields)
      .filter((field) => defaultFields[field] || hasValidValues(field))
      .map((field) => ({
        title: defaultFields[field] || formatFieldName(field),
        field: field,
        show: !!defaultFields[field],
        hidden: !defaultFields[field],
        export: true,
        cellStyle: { padding: "5px 0px 5px 2%" },
      }));
  };

  useEffect(() => {
    setTableData([]);
    setColumns([]);
    if (wageReportData?.length > 0) {
        const dynamicColumns = getRelevantFields(wageReportData);
        setColumns(dynamicColumns);
        setTableData(wageReportData?.map((item) => flattenData(item)));
    }
    const numericMonth = parseInt(selectedMonth, 10);
    const selectedDate = new Date(selectedYear, numericMonth - 1);
    setName(
      selectedDate.toLocaleString("default", {
        month: "short",
        year: "numeric",
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wageReportData]);

  const handleSendEmail = () => {
    setOpenPopup(true);
  };

  const handleColumnToggle = (field) => {
    setColumns((prevColumns) =>
      prevColumns.map((col) =>
        col.field === field
          ? { ...col, show: !col.show, hidden: !col.hidden }
          : col
      )
    );
  };
  const handlePopoverOpen = (event) => {
    setAnchorEl(anchorRef?.current);
  };
  const handlePopoverClose = () => {
    setAnchorEl(null);
    setSearchTerm("");
  };
  const handleExportAll = () => {
    const headers = columns?.map((col) => ({
      label: col.title,
      key: col.field,
    }));
    const csvData = [];
    csvData.push(["Pay Period", formatPayDatePeriod(paydate)]);
    csvData.push([]);

    csvData.push(headers.map((header) => header.label));
    const rowsToExport = wageReportData;
    rowsToExport?.forEach((row) => {
      const flattenedRow = flattenData(row);
      const rowData = headers.map((header) => flattenedRow[header.key] || "");
      csvData.push(rowData);
    });
    const csv = csvData.map((row) => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const downloadLink = document.createElement("a");
    downloadLink.href = url;
    downloadLink.download = `${name}.csv`;
    downloadLink.setAttribute("target", "_blank");
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    URL.revokeObjectURL(url);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value.toLowerCase());
  };
  const filteredColumns = columns?.filter((col) =>
    col.title.toLowerCase().includes(searchTerm)
  );

  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const visibleColumns = columns?.filter((col) => col.show);

  const filteredData = tableData?.filter((row) =>
    visibleColumns?.some((col) =>
      row[col.field]
        ?.toString()
        .toLowerCase()
        ?.includes(searchQuery.toLowerCase())
    )
  );
  const handleChangePage = (_, newPage) => {
    setCurrentPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(0);
  };

  return (
    <Box sx={{ overflowY: "auto"}} mt={4} mb={1}>
      <Box position="relative">
        <Box ref={anchorRef} sx={{ position: "absolute", top: 0, right: 0 }} />
        <Box
          width={
            isMobile
              ? "100%"
              : !fixedMenu && !open
              ? "calc(100vw - 75px)"
              : "calc(100vw - 260px)"
          }
          sx={{
            backgroundColor: "#ffffff",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            borderRadius: "8px",
          }}
        >
          {/* Header Section */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              backgroundColor: "#f3edfd",
              px: "15px",
              py: "10px"
            }}
          >
            {/* Title */}
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Employee
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                Action Completed
              </Typography>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
              <Box sx={{width: "180px", mx: "4px"}}>
                <SelectableMultiSelect
                    multiple
                    label="Work Location"
                    placeholder="Select Location"
                    value={filters?.ats_work_location_id}
                    loading={handleWorkLoacation}
                    fieldLabel="work_location_name"
                    name="ats_work_location_id"
                    size="small"
                    onChangeEvent={handleFilterChange}
                    data={worklocationData}
                    menuHeight={150} // optional
                />
              </Box>
              <TextField
                placeholder="Search"
                variant="standard"
                fullWidth
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <SearchIcon sx={{ mr: 1, color: "action.active" }} />
                  ),
                  endAdornment: searchQuery && (
                    <ClearIcon
                      sx={{ ml: 1, cursor: "pointer", color: "action.active" }}
                      onClick={() => setSearchQuery("")}
                    />
                  ),
                }}
                sx={{
                  width: "250px",
                  "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                }}
              />

              {wageReportData?.length > 0 && (
                <Tooltip title="Export as CSV">
                  <IconButton onClick={handleExportAll}>
                    <CloudDownloadIcon
                      sx={{ color: "#6d7c89", fontSize: "24px" }}
                    />
                  </IconButton>
                </Tooltip>
              )}

              {wageReportData?.length > 0 && (
                <Tooltip title="Send Email">
                  <IconButton onClick={handleSendEmail}>
                    <ForwardToInboxIcon
                      sx={{ color: "#6d7c89", fontSize: "24px" }}
                    />
                  </IconButton>
                </Tooltip>
              )}
              {wageReportData?.length > 0 && (
                <Tooltip title="Manage Columns">
                  <IconButton onClick={handlePopoverOpen}>
                    <ViewColumnIcon
                      sx={{ color: "#6d7c89", fontSize: "24px" }}
                    />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>

          {/* Content Section */}
          {isLoading ? (
            <Box
              sx={{
                height: 320,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "#f3edfd",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Table Section */}
              <TableContainer
                sx={{
                  minHeight: 320,
                  maxHeight: 320,
                  overflowY: "auto",
                }}
              >
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      {visibleColumns?.map((col) => (
                        <TableCell
                          key={col?.field}
                          style={{
                            fontSize: "13px",
                            fontWeight: "bold",
                            padding: "5px 0px 5px 10px",
                            textAlign: "start",
                            backgroundColor: "#ffffff",
                          }}
                          align="center"
                        >
                          {col?.title}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody sx={{ backgroundColor: "#f3edfd" }}>
                    {wageReportData?.length === 0 ? (
                      <Box
                        sx={{
                          height: 275,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#888",
                          fontSize: "16px",
                          backgroundColor: "#f3edfd",
                        }}
                      >
                        No records to display.
                      </Box>
                    ) : (
                      filteredData
                        ?.slice(
                          currentPage * rowsPerPage,
                          currentPage * rowsPerPage + rowsPerPage
                        )
                        ?.map((row, index) => (
                          <TableRow key={index}>
                            {visibleColumns?.map((col) => (
                              <TableCell
                                key={col?.field}
                                align="center"
                                style={{
                                  padding: "10px 0px 10px 15px",
                                  textAlign: "start",
                                }}
                              >
                                {/* {row[col?.field]} */}
                                {row[col?.field] || 0}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))
                    )}
                  </TableBody>
                  <Box
                    sx={{ minHeight: 275, backgroundColor: "#f3edfd" }}
                  ></Box>
                </Table>
              </TableContainer>

              {/* Pagination Section */}
              <TablePagination
                rowsPerPageOptions={[25, 50]}
                component="div"
                count={filteredData?.length}
                rowsPerPage={rowsPerPage}
                page={currentPage}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                showFirstButton
                showLastButton
                sx={{ backgroundColor: "#ede4fc" }}
                labelDisplayedRows={({ from, to, count, page }) =>
                  `Page ${page + 1} of ${Math?.ceil(
                    count / rowsPerPage
                  )} (${from}-${to} of ${count})`
                }
              />
            </>
          )}
        </Box>
      </Box>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Box
          sx={{
            width: "350px",
            maxHeight: "425px",
            overflowY: "auto",
          }}
        >
          {/* Search Field */}
          <Box
            sx={{
              padding: "12px 16px 0px 12px",
              position: "sticky",
              top: 0,
              backgroundColor: "white",
              zIndex: 1,
            }}
          >
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              placeholder="Type to search..."
              value={searchTerm}
              onChange={handleSearchChange}
              sx={{
                marginBottom: "8px",
                backgroundColor: "white",
                borderRadius: "4px",
              }}
              InputProps={{
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setSearchTerm("")} edge="end">
                      <CloseIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box
            sx={{
              minHeight: "87vh",
              overflowY: "auto",
              marginTop: "-10px",
            }}
          >
            <List>
              {filteredColumns?.length > 0 ? (
                filteredColumns
                  ?.slice()
                  ?.sort((a, b) => a.title.localeCompare(b.title))
                  ?.map((col) => (
                    <ListItem
                      key={col.field}
                      onClick={() => handleColumnToggle(col.field)}
                      sx={{ height: "40px", cursor: "pointer" }}
                    >
                      <ListItemIcon>
                        <Checkbox checked={col.show} />
                      </ListItemIcon>
                      <ListItemText primary={col.title} />
                    </ListItem>
                  ))
              ) : (
                <Typography
                  sx={{
                    textAlign: "center",
                    marginTop: "25px",
                  }}
                >
                  No results found
                </Typography>
              )}
            </List>
          </Box>
        </Box>
      </Popover>

      <Popup
        fullWidth={true}
        maxWidth="md"
        title="Pay Summary Report"
        openPopup={openPopup}
        setOpenPopup={setOpenPopup}
      >
        <ReportEmailPopup
          setOpenPopup={setOpenPopup}
          wageReportData={wageReportData}
          employees_options={columns}
          name={name}
        />
      </Popup>
    </Box>
  );
};

export default PayrunApprovedRegular;
